import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchCollection } from '../lib/cms';
import Lightbox from '../components/Lightbox';
import ContactForm from '../components/ContactForm';

// Statisk information om de enkelte ydelser til at berige CMS-galleridataene
const serviceDetailsMap = {
  'nybyggeri': {
    title: 'Nybyggeri',
    badge: 'Drømmebolig fra bunden',
    intro: 'Vi opfører moderne og funktionelle nybyggerier i højeste kvalitet tilpasset dine ønsker.',
    detailedText: [
      'At bygge et nyt hus er en af de største beslutninger i livet. Hos SKT-BYG ApS tilbyder vi professionelt tømrerarbejde i forbindelse med nybyggeri, uanset om der er tale om en totalentreprise eller fagentreprise. Vi styrer processen sikkert i mål, så du kan fokusere på at glæde dig til at flytte ind.',
      'Vi samarbejder tæt med dygtige arkitekter og ingeniører for at sikre, at alle tekniske specifikationer og æstetiske detaljer spiller perfekt sammen. Vi leverer håndværk i absolut særklasse med fokus på energioptimering, godt indeklima og langtidsholdbare materialer.'
    ],
    bullets: [
      'Totalentreprise og fagentreprise på nybyggeri',
      'Tæt samarbejde med arkitekter og ingeniører',
      'Moderne lavenergihuse og traditionelle parcelhuse',
      'Kvalitetsmaterialer og professionelt håndværk',
      'Overholdelse af tidsplaner og budgetter'
    ]
  },
  'om-tilbygning': {
    title: 'Om- og tilbygning',
    badge: 'Få mere plads i boligen',
    intro: 'Vi hjælper dig med at udvide dit eksisterende hjem med smukke og funktionelle tilbygninger.',
    detailedText: [
      'Mangler familien plads, eller trænger husets planløsning til en opdatering? En tilbygning eller en gennemgribende ombygning kan være den perfekte løsning til at skabe nyt liv i din eksisterende bolig. Vi har stor erfaring med at integrere nye tilbygninger, så de harmonerer smukt med det oprindelige hus\' arkitektur.',
      'Vi hjælper med alt fra ekstra børneværelser og store lyse køkken-alrum til garager eller en ny overetage. Vi lægger stor vægt på at bevare husets sjæl, samtidig med at vi opgraderer komforten til moderne standarder.'
    ],
    bullets: [
      'Tilbygning af ekstra værelser, stuer eller garage',
      'Ombygning og optimering af eksisterende rum',
      'Renovering af ældre huse med respekt for stilen',
      'Energioptimering v. ombygning og renovering',
      'Professionel rådgivning fra start til slut'
    ]
  },
  'vinduer-doere': {
    title: 'Vinduer og døre',
    badge: 'Energioptimering & nyt udtryk',
    intro: 'Professionel udskiftning og montering af døre og vinduer, der sikrer bedre isolering og lavere varmeregning.',
    detailedText: [
      'Gamle og utætte vinduer og døre er en af de største kilder til varmetab i boligen. Ved at udskifte dem til moderne lavenergiløsninger kan du reducere din varmeregning markant og samtidig opnå et væsentligt bedre indeklima uden træk og kuldebroer.',
      'Vi samarbejder med anerkendte producenter, hvilket garanterer dig produkter af højeste kvalitet med lang levetid. Uanset om du ønsker klassiske trævinduer eller moderne, vedligeholdelsesfrie træ/alu-elementer, finder vi den rette løsning.'
    ],
    bullets: [
      'Udskiftning af gamle vinduer og døre til moderne lavenergiløsninger',
      'Montering af skydedøre, terrassedøre og facadedøre',
      'Justering og reparation af eksisterende døre og vinduer',
      'Rådgivning om valg af materialer (træ, træ/alu, plast)',
      'Hurtig og effektiv montering uden unødigt svineri'
    ]
  },
  'terrasser-altaner': {
    title: 'Terrasser og altaner',
    badge: 'Udeliv i høj kvalitet',
    intro: 'Skræddersyede træterrasser og altaner, der forlænger sommeren og skaber fantastiske uderum.',
    detailedText: [
      'En flot og velkonstrueret træterrasse eller altan er den perfekte måde at udvide boligens opholdsareal på i de varme måneder. Vi designer og opbygger skræddersyede terrasser, der passer perfekt til din boligs arkitektur og havens naturlige terræn.',
      'Vi rådgiver dig grundigt om fordele og ulemper ved forskellige træsorter – fra eksklusive hårdttræssorter til klassisk lærk eller vedligeholdelsesfri komposit. Vi sikrer altid en solid underkonstruktion, som modstår det danske klima.'
    ],
    bullets: [
      'Opbygning af eksklusive træterrasser i hårdttræ eller lærk',
      'Etablering af hævede terrasser og tagterrasser',
      'Montering af altaner og gelændere i høj sikkerhed',
      'Skræddersyede løsninger tilpasset havens terræn',
      'Langtidsholdbare konstruktioner, der tåler det danske vejr'
    ]
  },
  'erhverv': {
    title: 'Erhverv',
    badge: 'Professionelle løsninger til virksomheder',
    intro: 'Vi udfører tømrerarbejde for erhvervskunder, detailforretninger og boligforeninger.',
    detailedText: [
      'Hos SKT-BYG ApS udfører vi alle former for tømrer- og snedkerarbejde for virksomheder, detailforretninger, kontorer og boligforeninger. Vi planlægger vores arbejde nøje, så vi minimerer forstyrrelser af jeres daglige drift.',
      'Vi tilbyder alt fra løbende vedligeholdelsesaftaler til større renoveringsprojekter, kontorindretning, akustikløsninger og butiksindretning. Vi lægger vægt på professionel projektstyring, overholdelse af tidsplaner og gennemsigtige budgetter.'
    ],
    bullets: [
      'Hustømrer- og viceværtaftaler for virksomheder',
      'Butiksindretning og specialinventar (diske, reoler, lysvægge)',
      'Kontorrenovering og akustikløsninger',
      'Vedligeholdelse af ejendomme og boligforeninger',
      'Fleksibel planlægning, der minimerer driftsforstyrrelser'
    ]
  },
  'kirker': {
    title: 'Kirker',
    badge: 'Respekt for historien & snedkerkunst',
    intro: 'Vi har stor erfaring med nænsomt tømrerarbejde og specialopgaver i historiske kirker.',
    detailedText: [
      'Arbejde i kirker og historiske bygninger kræver en helt særlig ydmyghed, tålmodighed og dyb forståelse for traditionelt snedker- og tømrerhåndværk. Vi har stor erfaring med nænsom vedligeholdelse og specialopgaver i kirkerum, hvor vi værner om de historiske detaljer.',
      'Vi udfører alt fra reparation af historiske tagkonstruktioner til specialfremstilling af nyt inventar (f.eks. skabe i våbenhuset eller tilpasning af kirkebænke) i tæt dialog med menighedsrådet og øvrige myndigheder.'
    ],
    bullets: [
      'Forefaldent tømrerarbejde og vedligeholdelse i kirker',
      'Opsætning af specialfremstillede skabe og inventar i våbenhuse',
      'Nænsom renovering af historiske trækonstruktioner',
      'Snedkerløsninger udført med traditionelle teknikker',
      'Tæt samarbejde med menighedsråd og stiftskonsulenter'
    ]
  },
  'mindre-opgaver': {
    title: 'Mindre opgaver',
    badge: 'Hurtig og effektiv hjælp',
    intro: 'Ingen opgave er for lille. Vi hjælper dig hurtigt i mål med dine mindre tømrer- og snedkerprojekter.',
    detailedText: [
      'Ingen opgave er for lille eller ubetydelig for os. Hos SKT-BYG ApS sætter vi en ære i at hjælpe vores kunder med de mindre tømrer- og snedkeropgaver i hverdagen – hvad enten det drejer sig om en dør, der binder, montering af nye fodpaneler eller mindre reparationer.',
      'Vi udbedrer også råd i vinduesrammer, udskifter beskadigede brædder på facaden eller monterer indvendige døre. Vi leverer altid den samme høje kvalitet og præcision, uanset opgavens størrelse.'
    ],
    bullets: [
      'Reparation og vedligeholdelse af træværk',
      'Montering af lofter, paneler og indvendige døre',
      'Mindre snedkerløsninger og tilpasninger',
      'Hurtig udrykning ved akutte skader',
      'Altid samme høje kvalitet, uanset opgavens størrelse'
    ]
  },
  'glarmester': {
    title: 'Glarmester & Glasarbejde',
    badge: 'Autoriseret glarmester',
    intro: 'Professionel udskiftning af ruder, termoglas og energiruder samt opsætning af glasværn og spejle.',
    detailedText: [
      'Som en del af vores samlede tømrer- og byggeydelser tilbyder vi professionelt glarmesterarbejde. Hvad enten du har fået en punkteret termorude, en knust rude eller ønsker at energioptimere boligen med moderne 3-lags energiglas, står vi klar med hurtig og præcis udskiftning.',
      'Vi leverer og monterer alle typer glasløsninger til både private og erhverv – herunder sikkerhedsglas, lyddæmpende glas, spejlvægge, glasdøre og elegante glasværn til terrasser og trapper. Vi sikrer altid tætte fuger og en holdbar finish.'
    ],
    bullets: [
      'Udskiftning af punkterede og knuste termoruder',
      'Montering af moderne energiglas med høj isoleringsevne',
      'Glasværn til terrasser, altaner og trapper',
      'Montering af New Yorker glasvægge og glasdøre',
      'Hurtig udrykning og præcis opmåling'
    ]
  },
  'forsikringsskader': {
    title: 'Forsikringsskader',
    badge: 'Hurtig skadesudbedring & taksering',
    intro: 'Vi udbedrer akutte skader og varetager hele processen og dialogen med dit forsikringsselskab.',
    detailedText: [
      'Når uheldet er ude i form af stormskader på taget, vandskader i gulve eller vægge, brandskader eller indbrud, er det vigtigt at handle hurtigt for at begrænse følgeskaderne. Hos SKT Byg rykker vi hurtigt ud for at sikre bygningen og påbegynde udbedringen.',
      'Med vores samlede tømrer- og murerekspertise kan vi løfte hele udbedringsforløbet fra start til slut. Vi dokumenterer skaden professionelt med billeder og rapporter, udarbejder overslag og har en tæt og smidig dialog med din taksator og dit forsikringsselskab.'
    ],
    bullets: [
      'Akut afdækning og skadesbegrænsning ved storm og skybrud',
      'Udbedring af vandskader, råd- og svampeskader',
      'Reparation efter indbrud og hærværk (døre, karme og vinduer)',
      'Tæt samarbejde og direkte dialog med forsikringsselskaber',
      'Både tømrer-, murer- og glarmesterarbejde samlet under ét'
    ]
  },
  'murerafdeling': {
    title: 'Murerafdeling',
    badge: 'Faglært murerarbejde & totalløsninger',
    intro: 'Vores erfarne murerafdeling leverer solidt håndværk – fra nybyggeri og badeværelser til facaderenovering og flisearbejde.',
    detailedText: [
      'Hos SKT-BYG ApS har vi vores egen stærke murerafdeling. Vores faglærte murersvende, Anders og Magnus, har mange års erfaring og sætter en ære i at levere murerarbejde af absolut højeste kvalitet.',
      'Vi udfører alle typer mureropgaver for både private boligejere, erhverv og boligforeninger. Vi dækker alt fra opmuring ved nybyggeri og tilbygninger til renovering af badeværelser med præcist flise- og klinkearbejde, pudsning af vægge, sokkelreparationer, vandskuring og facaderenovering.',
      'Når du vælger SKT-BYG til dit byggeprojekt, får du den store fordel, at tømrer- og murerarbejdet koordineres tæt under samme tag. Det sikrer en effektiv byggeproces uden unødige forsinkelser og et perfekt samspil mellem fagene.'
    ],
    bullets: [
      'Muring ved nybyggeri, tilbygninger og garager',
      'Komplet renovering og etablering af badeværelser',
      'Klinke- og flisearbejde i absolut højeste præcision',
      'Facaderenovering, omfugning, pudsning og vandskuring',
      'Sokkelarbejde, støbning og reparation af sætningsskader',
      'Tæt koordinering med vores tømrerafdeling for en komplet totalløsning'
    ]
  },
  'diverse': {
    title: 'Diverse ydelser',
    badge: 'Skræddersyede specialløsninger',
    intro: 'Vi løser en bred vifte af unikke og skræddersyede opgaver, der kræver ekstra kreativitet og præcision.',
    detailedText: [
      'Nogle projekter falder uden for de gængse kategorier, og det er netop her, vores alsidighed og kreative snedkerfærdigheder kommer i spil. Vi elsker specialopgaver, der kræver skræddersyede løsninger, utraditionelle materialer eller særlig præcision.',
      'Vi tilbyder blandt andet specialfremstilling af møbler, radiatorskjulere, indbyggede reolsystemer samt professionel rådgivning og gennemgang af boliger i forbindelse med huskøb.'
    ],
    bullets: [
      'Individuelle snedkerløsninger og specialinventar',
      'Gennemgang af bolig inden huskøb',
      'Akustikberegninger og støjreduktion',
      'Isolering og energivejledning',
      'Andre typer skræddersyede håndværksopgaver'
    ]
  }
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [gallery, setGallery] = useState(null);
  const [cmsService, setCmsService] = useState(null);
  const [loading, setLoading] = useState(true);

  // Lightbox tilstand
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [allGalleries, allServices] = await Promise.all([
          fetchCollection('galleries'),
          fetchCollection('services').catch(() => [])
        ]);

        const matchedGallery = allGalleries.find(g => g.slug?.da === slug || g.title?.da?.toLowerCase() === slug);
        if (matchedGallery) {
          setGallery(matchedGallery);
        }

        // Match service fra CMS baseret på slug eller titel-nøgle
        const matchedService = allServices?.find(s => {
          if (s.slug?.da === slug || s.slug?.da?.startsWith(slug)) return true;
          const cleanTitle = s.title?.da?.toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
          const cleanSlug = slug.toLowerCase().replace(/[^a-z0-9]+/g, '-');
          return cleanTitle === cleanSlug || cleanSlug.includes(cleanTitle) || cleanTitle.includes(cleanSlug);
        });

        if (matchedService) {
          setCmsService(matchedService);
        }

        // Hvis der hverken findes et galleri, cms-service eller statisk info, gå til 404
        if (!matchedGallery && !matchedService && !serviceDetailsMap[slug]) {
          navigate('/404');
        }
      } catch (err) {
        console.error('Error loading service detail:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [slug, navigate]);

  const defaultStatic = serviceDetailsMap[slug] || {
    title: gallery?.title?.da || 'Ydelse',
    badge: 'Kvalitetshåndværk',
    intro: 'Vi sikrer dig kvalitet og godt håndværk inden for alle tømreropgaver.',
    detailedText: [
      'Hos SKT-BYG ApS har vi mere end 30 års erfaring med alle typer opgaver inden for tømrerfaget. Vi vægter kvalitet, præcision og god dialog med kunden utrolig højt, så vi sikrer det bedste resultat hver gang.',
      'Vi rådgiver dig grundigt fra start til slut, uanset om det drejer sig om et mindre renoveringsprojekt eller en større totalentreprise. Vores mål er altid at levere et resultat, der overstiger dine forventninger.'
    ],
    bullets: [
      'Professionelt udført tømrerarbejde',
      'Kvalitetsmaterialer og præcision',
      'Overholdelse af aftaler og tidsplaner',
      'Skræddersyede løsninger efter dine behov'
    ]
  };

  const serviceTitle = cmsService?.title?.da || defaultStatic.title;
  const serviceBadge = cmsService?.badge?.da || defaultStatic.badge;
  const serviceIntro = cmsService?.short_description?.da || cmsService?.short_description || defaultStatic.intro;
  const serviceContent = cmsService?.content?.da || cmsService?.content || gallery?.description?.da;
  const serviceBulletsHtml = cmsService?.bullet_points?.da || cmsService?.bullet_points;

  const processTitle = cmsService?.process_title?.da || cmsService?.process_title || "Vores arbejdsproces – Fra idé til færdigt resultat";
  const step1Title = cmsService?.process_step_1_title?.da || cmsService?.process_step_1_title || "Rådgivning & Opmåling";
  const step1Text = cmsService?.process_step_1_text?.da || cmsService?.process_step_1_text || "Vi starter altid med en grundig snak om dine ønsker og behov, hvor vi rådgiver om materialevalg og løsninger.";
  const step2Title = cmsService?.process_step_2_title?.da || cmsService?.process_step_2_title || "Uforpligtende Tilbud";
  const step2Text = cmsService?.process_step_2_text?.da || cmsService?.process_step_2_text || "Du modtager et skriftligt og gennemskueligt tilbud med en fast tidsplan, så du kender alle detaljer på forhånd.";
  const step3Title = cmsService?.process_step_3_title?.da || cmsService?.process_step_3_title || "Udførelse & Aflevering";
  const step3Text = cmsService?.process_step_3_text?.da || cmsService?.process_step_3_text || "Vores svende udfører arbejdet efter højeste standarder og rydder pænt op, før vi sammen gennemgår det færdige resultat.";

  const handleOpenLightbox = (index) => {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  };

  const handlePrevImage = () => {
    if (!gallery?.images) return;
    setLightboxIndex((prev) => (prev === 0 ? gallery.images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    if (!gallery?.images) return;
    setLightboxIndex((prev) => (prev === gallery.images.length - 1 ? 0 : prev + 1));
  };

  const metaTitle = cmsService?.meta_title?.da || cmsService?.meta_title || `${serviceTitle} i Galten & omegn | SKT Byg`;
  const metaDesc = cmsService?.meta_description?.da || cmsService?.meta_description || `${serviceIntro ? serviceIntro + ' ' : ''}Professionelt udført af SKT Byg i Galten, Silkeborg og omegn. Se billeder og få et uforpligtende tilbud.`;

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-light flex items-center justify-center">
        <div className="text-center space-y-4">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-accent"></i>
          <p className="text-brand-dark/60 font-semibold">Henter information...</p>
        </div>
      </div>
    );
  }

  const heroImage = (cmsService?.image && cmsService.image[0]) || gallery?.images?.[0] || "https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/slider-1.jpg";

  return (
    <div className="min-h-screen bg-brand-light">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={heroImage} />
      </Helmet>

      {/* Hero Sektion */}
      <section className="relative bg-brand-dark text-white pt-12 pb-10 md:pt-16 md:pb-12 lg:pt-20 lg:pb-16 overflow-hidden">
        {/* Baggrundsbillede med mørkt overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt={serviceTitle}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/90 via-brand-dark/60 to-transparent"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 uppercase leading-none">
              {serviceTitle}
            </h1>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed font-medium">
              {serviceIntro}
            </p>
          </div>
        </div>
      </section>

      {/* Detaljeret indhold & Bullets */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Venstre side: Beskrivelse & tjekliste */}
          <div className="lg:col-span-7 space-y-8">
            <div className="prose prose-lg text-brand-dark/80 leading-relaxed space-y-6">
              <h2 className="text-2xl md:text-3xl font-extrabold text-brand-dark uppercase mb-6">
                Hvad vi tilbyder inden for {serviceTitle.toLowerCase()}
              </h2>
              {serviceContent ? (
                <div dangerouslySetInnerHTML={{ __html: serviceContent }} />
              ) : (
                defaultStatic.detailedText.map((p, idx) => (
                  <p key={idx} className="text-brand-dark/80">
                    {p}
                  </p>
                ))
              )}
            </div>

            <div className="bg-brand-sand/30 border border-brand-sand p-8">
              <h3 className="font-bold text-brand-dark uppercase mb-4 text-lg">
                Vores kompetencer omfatter:
              </h3>
              {serviceBulletsHtml ? (
                <div className="prose prose-sm text-brand-dark/80" dangerouslySetInnerHTML={{ __html: serviceBulletsHtml }} />
              ) : (
                <ul className="space-y-3">
                  {defaultStatic.bullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm font-semibold text-brand-dark/80">
                      <i className="fa-solid fa-check text-brand-dark mt-1 shrink-0"></i>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Ny proces-sektion for at berige indholdet */}
            <div className="border-t border-brand-sand pt-10 mt-10">
              <h3 className="text-xl font-bold text-brand-dark uppercase mb-6">
                {processTitle}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <div className="w-8 h-8 rounded-full bg-brand-dark text-white flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                  <h4 className="font-bold text-brand-dark uppercase text-sm">{step1Title}</h4>
                  <p className="text-xs text-brand-dark/70 leading-relaxed">
                    {step1Text}
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="w-8 h-8 rounded-full bg-brand-dark text-white flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <h4 className="font-bold text-brand-dark uppercase text-sm">{step2Title}</h4>
                  <p className="text-xs text-brand-dark/70 leading-relaxed">
                    {step2Text}
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="w-8 h-8 rounded-full bg-brand-dark text-white flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <h4 className="font-bold text-brand-dark uppercase text-sm">{step3Title}</h4>
                  <p className="text-xs text-brand-dark/70 leading-relaxed">
                    {step3Text}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Højre side: Kontaktformular */}
          <div className="lg:col-span-5">
            <h3 className="text-xl font-bold text-brand-dark uppercase mb-2">
              Få et uforpligtende tilbud
            </h3>
            <p className="text-sm text-brand-dark/70 mb-6">
              Udfyld formularen, så vender vi hurtigt tilbage med et uforpligtende tilbud på {serviceTitle.toLowerCase()}.
            </p>
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Galleri / Billeder */}
      {gallery?.images && gallery.images.length > 0 && (
        <section className="py-16 bg-brand-sand/20 border-t border-brand-sand">
          <div className="mx-auto max-w-7xl px-6">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl font-extrabold text-brand-dark uppercase">
                Se vores arbejde med {serviceTitle.toLowerCase()}
              </h2>
              <p className="text-brand-dark/70 mt-2">
                Klik på billederne nedenfor for at se dem i fuld størrelse.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {gallery.images.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => handleOpenLightbox(idx)}
                  className="relative h-64 overflow-hidden border border-brand-sand shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer group"
                >
                  <img
                    src={img}
                    alt={`${serviceTitle} reference ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <i className="fa-solid fa-magnifying-glass-plus text-white text-2xl"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      {isLightboxOpen && gallery?.images && (
        <Lightbox
          images={gallery.images}
          currentIndex={lightboxIndex}
          onClose={() => setIsLightboxOpen(false)}
          onPrev={handlePrevImage}
          onNext={handleNextImage}
        />
      )}
    </div>
  );
}
